// AnnualLeaveTableMobile.tsx
import "./AnnualLeaveTableMobile.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Collapse, Input, Popover, Tooltip, theme } from "antd";
import type { InputRef } from "antd";
import {
  DeleteOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { months } from "../../utils/constants";
import type { AnnualPlanItem, BankHolidays, LeaveRequest, Stock } from "../../utils/types";

type Props = {
  annualLeaveYear: string;
  users: Stock[];
  sortOrder: string | null;
  selectedLeaveTypes: string[];
  bankHolidays: BankHolidays[];
  leaveRequests: LeaveRequest[];
  legendColors: Record<string, string>;
  annualPlan: AnnualPlanItem[];
};

const PENDING_STATUSES = new Set([
  "AWAITING_MANAGER",
  "AWAITING_ADMINISTRATIV",
  "RETURNED_FOR_CORRECTION",
  "AWAITING_DOCUMENT_REVIEW",
]);
const FINAL_APPROVED_STATUSES = new Set(["APPROVED"]);

type UserGroup = {
  employeeName: string;
  remainingDays: number | string;
  requests: LeaveRequest[];
};

const weekday2 = (d: Date) =>
  d.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 2);

const buildLeaveDayInfoMap = (requests: LeaveRequest[]) => {
  const map = new Map<
    string,
    { type: string; comment: string; substituteName: string; status: string }
  >();
  requests.forEach((lq) => {
    lq.leaveDays.forEach((day) => {
      map.set(day, {
        type: lq.requestType,
        comment: lq.comment ?? "",
        substituteName: lq.substituteName ?? "",
        status: lq.requestStatus,
      });
    });
  });
  return map;
};

const AnnualLeaveTableMobile = ({
  annualLeaveYear,
  users,
  sortOrder,
  selectedLeaveTypes,
  bankHolidays,
  leaveRequests,
  legendColors,
  annualPlan,
}: Props) => {
  const { token } = theme.useToken();

  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");
  const [willSearchName, setWillSearchName] = useState(false);

  const inputRef = useRef<InputRef>(null);

  const year = useMemo(() => parseInt(annualLeaveYear, 10), [annualLeaveYear]);
  const monthsList = useMemo(() => months(year), [year]);
  const currentMonthIndex = new Date().getMonth();
  const monthRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchName(searchName), 1000);
    return () => clearTimeout(handler);
  }, [searchName]);

  useEffect(() => {
    if (!willSearchName) return;
    // AntD Input ref typing is messy; focus via DOM
    const el = document.querySelector(
      ".al-table-mobile .search-input input",
    ) as HTMLInputElement | null;
    el?.focus();
  }, [willSearchName]);

  const groupedArray = useMemo<UserGroup[]>(() => {
    const grouped = leaveRequests.reduce(
      (acc, item) => {
        const name = item.employeeName;
        const remainingDays = item.currentYearRemainingDays;
        if (!acc[name]) {
          acc[name] = { employeeName: name, remainingDays, requests: [] };
        }
        acc[name].requests.push(item);
        return acc;
      },
      {} as Record<string, UserGroup>,
    );

    const groupedNames = new Set(Object.keys(grouped));

    users.forEach((u) => {
      if (!groupedNames.has(u.employeeName)) {
        grouped[u.employeeName] = {
          employeeName: u.employeeName,
          remainingDays: u.availableDays,
          requests: [
            {
              id: Math.random(),
              employeeName: u.employeeName,
              startDate: "",
              endDate: "",
              requestStatus: "",
              requestType: "",
              leaveDays: [],
              comment: "",
              substituteName: "",
              currentYearRemainingDays: u.availableDays,
            },
          ],
        };
      }
    });

    return Object.values(grouped);
  }, [leaveRequests, users]);

  const sortedGroupedArray = useMemo(() => {
    const filtered = groupedArray.filter((u) =>
      u.employeeName.toLowerCase().includes(debouncedSearchName.toLowerCase()),
    );

    return filtered.slice().sort((a, b) => {
      if (a.employeeName < b.employeeName) return sortOrder === "asc" ? -1 : 1;
      if (a.employeeName > b.employeeName) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupedArray, sortOrder, debouncedSearchName]);

  const bankHolidayMap = useMemo(() => {
    return new Map(
      bankHolidays.map((h) => [
        new Date(h.date).toLocaleDateString("sv-SE"),
        h.description,
      ]),
    );
  }, [bankHolidays]);

  const annualPlanByEmployee = useMemo(() => {
    const map = new Map<string, Set<string>>();
    annualPlan.forEach(({ employeeName, startDate, endDate }) => {
      if (!employeeName) return;
      const dates = map.get(employeeName) ?? new Set<string>();
      for (
        let d = new Date(startDate);
        d <= new Date(endDate);
        d.setDate(d.getDate() + 1)
      ) {
        dates.add(d.toLocaleDateString("sv-SE"));
      }
      map.set(employeeName, dates);
    });
    return map;
  }, [annualPlan]);

  const getCellMeta = useCallback(
    ({
      date,
      formattedDate,
      leaveDayInfoMap,
      annualPlanDates,
    }: {
      date: Date;
      formattedDate: string;
      leaveDayInfoMap: Map<
        string,
        { type: string; comment: string; substituteName: string; status: string }
      >;
      annualPlanDates: Set<string>;
    }) => {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const leaveInfo = leaveDayInfoMap.get(formattedDate);
      const leaveType = leaveInfo?.type;
      const status = leaveInfo?.status;

      const isPending = !!status && PENDING_STATUSES.has(status);
      const isApproved = !!status && FINAL_APPROVED_STATUSES.has(status);
      const isAnnualPlanDay =
        annualPlanDates.has(formattedDate) &&
        selectedLeaveTypes.includes("ANNUAL_PLAN");

      // hide cells when status filter is off
      if (isPending && !selectedLeaveTypes.includes("PENDING") && !isAnnualPlanDay) {
        return {
          hidden: true,
          className: "day hidden-cell",
          popup: "",
          onClick: undefined as undefined,
          hasRequest: true,
          isApproved: false,
          isPending: false,
          style: undefined as React.CSSProperties | undefined,
        };
      }

      if (isApproved && !selectedLeaveTypes.includes("APPROVED") && !isAnnualPlanDay) {
        return {
          hidden: true,
          className: "day hidden-cell",
          popup: "",
          onClick: undefined as undefined,
          hasRequest: true,
          isApproved: false,
          isPending: false,
          style: undefined as React.CSSProperties | undefined,
        };
      }

      const isLeaveDay = !!leaveType && selectedLeaveTypes.includes(leaveType);
      const hasRequest = !!leaveType;

      const bankHolidayDescription = bankHolidayMap.get(formattedDate);
      const isBankHoliday =
        !!bankHolidayDescription && selectedLeaveTypes.includes("BANK_HOLIDAY");

      let popup = "";
      if (isBankHoliday) {
        popup = `Holiday: ${bankHolidayDescription}`;
      } else if (hasRequest && (isPending || isApproved)) {
        popup = `${leaveInfo?.type} - ${
          leaveInfo?.comment || "No comment"
        } | Substitute: ${leaveInfo?.substituteName || "N/A"} | Status: ${status}`;
      } else if (hasRequest) {
        popup = `${leaveInfo?.type} - ${
          leaveInfo?.comment || "N/A"
        } | Substitute: ${leaveInfo?.substituteName || "N/A"}`;
      } else if (isAnnualPlanDay) {
        popup = "Annual Plan Day";
      } else if (isWeekend) {
        popup = "Weekend";
      }

      const className = [
        "day",
        isBankHoliday ? "BANK_HOLIDAY" : "",
        isWeekend ? "weekend" : "",
        isAnnualPlanDay ? "annual-plan-day" : "",
      ]
        .filter(Boolean)
        .join(" ");

      // Cell color comes from the per-type legend color (see
      // LeaveConfig.tsx), status is layered on top as opacity.
      let style: React.CSSProperties | undefined;
      if (isBankHoliday) {
        style = { backgroundColor: legendColors.BANK_HOLIDAY };
      } else if (isLeaveDay && leaveType) {
        style = {
          backgroundColor: legendColors[leaveType] || "#9aa5b1",
          opacity: isPending ? 0.55 : 1,
        };
      }

      const allowClick = !isWeekend && !isLeaveDay && !isBankHoliday;

      return {
        hidden: false,
        className,
        popup,
        onClick: allowClick ? undefined : undefined,
        hasRequest,
        isApproved,
        isPending,
        style,
      };
    },
    [bankHolidayMap, legendColors, selectedLeaveTypes],
  );

  const infoBtnMsg =
    "Calculation:\n(Current year days + Previous year days) - Used current year days - Used previous year days";

  useEffect(() => {
    const el = monthRefs.current[currentMonthIndex];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentMonthIndex, annualLeaveYear]);

  const toggleSearch = () => {
    setWillSearchName((v) => !v);
    setSearchName("");
    setDebouncedSearchName("");
  };

  return (
    <div
      className="al-table-mobile"
      style={
        {
          "--al-bg": token.colorBgContainer,
          "--al-bg-subtle": token.colorFillTertiary,
          "--al-border": token.colorBorderSecondary,
          "--al-text": token.colorText,
          "--al-text-secondary": token.colorTextSecondary,
          "--al-approved": token.colorSuccess,
          "--al-pending": token.colorInfo,
          "--al-holiday": token.colorError,
        } as React.CSSProperties & Record<`--${string}`, string>
      }
    >
      <div className="topbar">
        <div className="title">Annual Leave</div>

        <div className={`search ${willSearchName ? "show" : ""}`}>
          {willSearchName ? (
            <div className="search-row">
              <Input
                className="search-input"
                placeholder="Search..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                ref={inputRef}
              />
              <Button
                className="delete-icon"
                type="text"
                icon={<DeleteOutlined />}
                onClick={toggleSearch}
              />
            </div>
          ) : (
            <Button
              type="text"
              className="search-icon"
              icon={<SearchOutlined />}
              onClick={toggleSearch}
            />
          )}
        </div>

        <div className="info">
          <Tooltip title={<span className="info-tooltip">{infoBtnMsg}</span>}>
            <Button
              className="info-btn"
              type="text"
              icon={<QuestionCircleOutlined />}
            />
          </Tooltip>
        </div>
      </div>

      <Collapse
        className="users"
        accordion={false}
        items={sortedGroupedArray.map((u, idx) => {
          const leaveDayInfoMap = buildLeaveDayInfoMap(u.requests);
          const annualPlanDates =
            annualPlanByEmployee.get(u.employeeName) ?? new Set<string>();

          const label = (
            <div className="user-title">
              <div className="name">{u.employeeName}</div>
              <div className="remaining">
                <span className="label">Remaining</span>
                <span className="value">{u.remainingDays ?? "—"}</span>
              </div>
            </div>
          );

          return {
            key: String(idx),
            label,
            children: (
              <div className="user-body">
                <Collapse
                  className="months"
                  defaultActiveKey={[String(currentMonthIndex)]}
                  items={monthsList.map((m, monthIndex) => {
                    const monthTitle = (
                      <div className="month-title">
                        <span>{m.name}</span>
                        <span className="range">1-{m.days}</span>
                      </div>
                    );

                    return {
                      key: String(monthIndex),
                      label: monthTitle,
                      children: (
                        <div
                          ref={(el) => {
                            // scroll-to current month for the first (opened) user
                            if (idx === 0) monthRefs.current[monthIndex] = el;
                          }}
                          className="month-body"
                        >
                          <div
                            className="days-strip"
                            role="grid"
                            aria-label={`${u.employeeName} ${m.name} ${annualLeaveYear}`}
                          >
                            {Array.from({ length: m.days }, (_, dayIndex) => {
                              const date = new Date(year, monthIndex, dayIndex + 1);
                              const formattedDate =
                                date.toLocaleDateString("sv-SE");

                              const meta = getCellMeta({
                                date,
                                formattedDate,
                                leaveDayInfoMap,
                                annualPlanDates,
                              });

                              if (meta.hidden) {
                                return (
                                  <div
                                    key={dayIndex}
                                    className="day hidden-cell"
                                    aria-hidden="true"
                                  />
                                );
                              }

                              const content = meta.popup ? (
                                <div className="popover-content">
                                  <div className="popover-tags">
                                    {meta.hasRequest && meta.isPending && (
                                      <span className="tag pending">Pending</span>
                                    )}
                                    {meta.hasRequest && meta.isApproved && (
                                      <span className="tag approved">Approved</span>
                                    )}
                                    {meta.className.includes("BANK_HOLIDAY") && (
                                      <span className="tag holiday">Holiday</span>
                                    )}
                                  </div>
                                  <div>{meta.popup}</div>
                                </div>
                              ) : null;

                              const dayNode = (
                                <button
                                  type="button"
                                  key={dayIndex}
                                  className={meta.className}
                                  style={meta.style}
                                  onClick={meta.onClick}
                                  disabled={!meta.onClick}
                                  aria-label={`${dayIndex + 1} ${m.name} ${annualLeaveYear}`}
                                >
                                  <span className="day-num">{dayIndex + 1}</span>
                                  <span className="day-dow" aria-hidden="true">
                                    {weekday2(date)}
                                  </span>
                                </button>
                              );

                              return meta.popup ? (
                                <Popover
                                  key={dayIndex}
                                  content={content}
                                  trigger="click"
                                  placement="top"
                                >
                                  {dayNode}
                                </Popover>
                              ) : (
                                dayNode
                              );
                            })}
                          </div>

                          <div className="legend">
                            <Badge status="success" text="Approved" />
                            <Badge status="processing" text="Pending" />
                            <Badge status="error" text="Holiday" />
                            <Badge status="default" text="Weekend / blocked" />
                          </div>
                        </div>
                      ),
                    };
                  })}
                />
              </div>
            ),
          };
        })}
      />
    </div>
  );
};

export default AnnualLeaveTableMobile;
